import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Box, CircularProgress, Typography } from "@mui/material";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";

const API = process.env.REACT_APP_API_URL;

const normalizeList = (data) => (Array.isArray(data) ? data : data?.results || []);

const StudentBedSeatNotes = () => {
  const [hostelNotes, setHostelNotes] = useState([]);
  const [libraryNotes, setLibraryNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      setLoadError("");
      const [hostelResult, libraryResult] = await Promise.allSettled([
        axios.get(`${API}hostel/my-bed-notes/`, config),
        axios.get(`${API}library/my-seat-notes/`, config),
      ]);

      if (hostelResult.status === "fulfilled") {
        setHostelNotes(normalizeList(hostelResult.value.data));
      } else {
        setHostelNotes([]);
        console.error("Unable to load assigned bed notes", hostelResult.reason);
      }

      if (libraryResult.status === "fulfilled") {
        setLibraryNotes(normalizeList(libraryResult.value.data));
      } else {
        setLibraryNotes([]);
        console.error("Unable to load assigned seat notes", libraryResult.reason);
      }

      if (hostelResult.status === "rejected" && libraryResult.status === "rejected") {
        setLoadError("Unable to load your assigned Bed/Seat notes.");
      }
    } catch (error) {
      setHostelNotes([]);
      setLibraryNotes([]);
      setLoadError("Unable to load your assigned Bed/Seat notes.");
      console.error("Unable to load assigned bed/seat notes", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const sections = [
    { key: "hostel", title: "My Bed Instructions", notes: hostelNotes },
    { key: "library", title: "My Seat Instructions", notes: libraryNotes },
  ];

  if (loading) {
    return (
      <Box className="bg-white rounded-xl shadow-lg p-5 flex items-center gap-3">
        <CircularProgress size={22} />
        <Typography>Loading your bed and seat notes...</Typography>
      </Box>
    );
  }

  if (hostelNotes.length === 0 && libraryNotes.length === 0) {
    return (
      <Box className="bg-white rounded-xl shadow-lg p-4 md:p-6 dashboard-card">
        <Box className="flex items-center gap-3">
          <NotesOutlinedIcon color="action" />
          <Box>
            <Typography variant="h6" className="font-bold text-gray-800">
              Bed / Seat Notes
            </Typography>
            <Typography variant="body2" color={loadError ? "error" : "text.secondary"}>
              {loadError || "No active notes are available for your currently approved Bed or Seat."}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="bg-white rounded-xl shadow-lg p-4 md:p-6 dashboard-card">
      <Box className="flex items-center gap-3 mb-4">
        <Box className="p-2 md:p-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl">
          <NotesOutlinedIcon className="text-white" />
        </Box>
        <Box>
          <Typography variant="h6" className="font-bold text-gray-800">
            Bed / Seat Notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Instructions shared by the administration for your current allocation.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {sections.filter((section) => section.notes.length > 0).map((section) => (
          <Box key={section.key} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
              {section.title}
            </Typography>
            {section.notes.map((note) => (
              <Box key={note.id} sx={{ mb: 1.5 }}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ mb: 0.75 }}
                >
                  {section.key === "hostel"
                    ? `Room ${note.room_number} / Bed ${note.bed_number}`
                    : `Seat ${note.seat_number}`}
                </Typography>

                <Box
                  sx={{
                    backgroundColor: "#FFF7ED",
                    border: "1px solid #FED7AA",
                    borderLeft: "4px solid #F97316",
                    borderRadius: "8px",
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#C2410C",
                      fontWeight: 500,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    <strong>Note:</strong> {note.note}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default StudentBedSeatNotes;
